import { inject, Injectable } from '@angular/core';
import { IResponse } from '@webapp/core/interfaces/response.interface';
import { protectedContext, RequestOptions } from '@webapp/core/helpers/request-context';
import { ProtectedService } from '@webapp/core/services/protected.service';
import { PaginationParams } from '@webapp/core/ui/components/pagination/pagination.interfaces';
import { ToastService } from '@webapp/ui';
import { map, Observable } from 'rxjs';
import { IPoliciesPage} from '../interfaces/policies.interface';
import {
	ClientPolicyStatus,
	DEFAULT_CLIENT_POLICY_STATUSES,
	IClientPolicy,
} from '../interfaces/client-policy.interface';
import { IQuoterPolicyEmition, IQuoterPolicy } from '@webapp/quoter/interfaces/quoter.interface';
import { IEndorsements } from '@webapp/endorsements';
import { IBasePolicy } from '../interfaces/policies.interface';

@Injectable({
	providedIn: 'root',
})
export class PoliciesService extends ProtectedService {
	toastService = inject(ToastService);
	get(params: PaginationParams): Observable<IPoliciesPage> {
		return this.http
			.get<IResponse>(`${this.apiUrl}policies`, {
				...this.CONTEXT,
				params: {
					limit: params.limit?.toString() || 10,
					offset: params.offset?.toString() || 0,
					search: params.search || '',
					filters: JSON.stringify(params.filters),
				},
			})
			.pipe(
				map((response: IResponse) => ({
					data: response.data.rows,
					pagination: response.data.pagination,
				})),
			);
	}

	getById(id: string, options?: RequestOptions): Observable<IBasePolicy> {
		return this.http
			.get<IResponse>(`${this.apiUrl}policies/${id}`, {
				...protectedContext(options),
			})
			.pipe(
				map(response => response.data),
			);
	}

	getEndorsements(id: string): Observable<IEndorsements[]> {
		return this.http
			.get<IResponse>(`${this.apiUrl}policies/${id}/endorsements`, {
				...this.CONTEXT,
			})
			.pipe(
				map(response => response.data),
			);
	}

	download(id: string): Observable<Blob> {
		return this.http
			.get(`${this.apiUrl}policies/${id}/certificate`, {
				...this.CONTEXT,
				responseType: 'blob',
			})
			.pipe(
				map((response: Blob) => response),
			);
	}

	/**
	 * Returns a TEMPORARY url (the file is deleted server-side after ~3 min).
	 * Never cache: fetch on every use.
	 */
	getCertificateUrl(id: string, options?: RequestOptions): Observable<string> {
		return this.http
			.get<IResponse>(`${this.apiUrl}policies/${id}/certificate`, {
				...protectedContext(options),
				params: { type: 'url' },
			})
			.pipe(map(response => response.data as string));
	}

	changePaymentMethod(policyId: string, paymentMethodId: string) {
		return this.http
			.put<IResponse>(
				`${this.apiUrl}policies/${policyId}/payment-method`,
				{paymentMethodId: paymentMethodId},
				this.CONTEXT
			)
			.pipe(
				map(response => response.data),
			);
	}

	changeClientInfo(
		policyId: string,
		clientInfo: {
			firstName: string;
			lastName: string;
			dni: string;
			birthDate: string;
			address: string;
			phone: string;
		}
	) {
		return this.http
			.put<IResponse>(
				`${this.apiUrl}policies/${policyId}/client`,
				clientInfo,
				this.CONTEXT
			)
			.pipe(
				map(response => response.data),
			);
	}

	changeVehicleInfo(
		policyId: string,
		vehicleInfo: {
			plate: string;
			motor: string;
			chasis: string;
		}
	) {
		return this.http
			.put<IResponse>(
				`${this.apiUrl}policies/${policyId}/vehicle`,
				vehicleInfo,
				this.CONTEXT
			)
			.pipe(
				map(response => response.data),
			);
	}

	updatePolicyInsurable(
		policyId: string,
		insurableData: Partial<{
			plate: string;
			motor: string;
			chasis: string;
		}>
	) {
		return this.http
			.put<IResponse>(
				`${this.apiUrl}policies/${policyId}/insurable`,
				insurableData,
				this.CONTEXT
			)
			.pipe(
				map(response => response.data)
			);
	}

	addGarage(
		policyId: string,
	) {
		return this.http
			.post<IResponse>(
				`${this.apiUrl}policies/${policyId}/add-garage`, {},
				this.CONTEXT
			)
			.pipe(
				map(response => response.data)
			);
	}

	getMyPolicies(
		options?: RequestOptions,
		status: readonly ClientPolicyStatus[] = DEFAULT_CLIENT_POLICY_STATUSES,
	): Observable<IClientPolicy[]> {
		return this.http
			.get<IResponse>(`${this.apiUrl}policies/me`, {
				...protectedContext(options),
				params: { status: [...status] },
			})
			.pipe(map(response => response.data));
	}

	/**
	 * Emits a new policy
	 * @param policyPayload The policy data for emission
	 * @param quoteId The quote ID associated with the policy
	 * @returns Observable with the emitted policy data
	 */
	emitPolicy(
		policyPayload: IQuoterPolicyEmition,
		quoteId: string
	): Observable<IQuoterPolicy> {
		return this.http
			.post<IResponse>(
				`${this.apiUrl}policies/${quoteId}/beta`,
				policyPayload,
				this.CONTEXT
			)
			.pipe(
				map((response: IResponse) => response.data)
			);
	}
}
